import { useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Typography, makeStyles } from '@material-ui/core';
import GeneralMoodChart from '../generalMoodChart';
import Tooltip from '../tooltip';
import UserLink from '../userLink';
import StatusIndicator from '../statusIndicator';
import { ReactComponent as TooltipIcon } from '../../../assets/icons/info-dark.svg';
import { ACTION_PLAN_STATUSES_LIST } from '../../../constants/statuses';
import { PERMISSION_CODES } from '../../../constants/permissions';
import { prepareUserMoodData } from '../../../utility/dashboard';
import { getTotalNotesIndicator } from './config';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    backgroundColor: primary.white,
    boxSizing: 'border-box',
    borderRadius: 16,
    display: 'flex',
    flexDirection: 'column',
    padding: spacing(4, 4, 4, 4),
    width: 205,
    height: 276,
    userSelect: 'none',
    position: 'relative',
  },
  rootNoCourses: {
    height: 232,
  },
  headerWrapper: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: spacing(6),
    width: '100%',
  },
  avatar: {
    position: 'absolute',
    left: 16,
    top: -8,
  },
  caption: {
    justifyContent: 'flex-start',
    maxWidth: '100%',
    marginTop: spacing(2),
    marginLeft: 0,
    minHeight: 36,
  },
  user: {
    maxWidth: '100%',
  },
  name: {
    lineHeight: '16px',
  },
  jobTitle: {
    fontSize: 12,
    lineHeight: '16px',
  },
  statsWrapper: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0,1fr))',
    gridColumnGap: 4,
    rowGap: 12,
  },
  labelWrapper: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: spacing(2),
  },
  label: {
    lineHeight: '16px',
  },
  scoreWrapper: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: spacing(1.5),
    '&:last-of-type': {
      marginBottom: 0,
    },
  },
  scoreValue: {
    maxWidth: 20,
    width: 'fit-content',
    flexShrink: 0,
    marginRight: spacing(1),
  },
  tooltipIcon: {
    height: 14,
    width: 14,
    marginLeft: spacing(1),
  },
  section: {
    cursor: 'default',
  },
  sectionAccessible: {
    cursor: 'pointer',
  },
  noResults: {
    opacity: 0.3,
  },
}));

const TeamUserStatsCard = ({
  className = undefined,
  translations,
  user = {},
  courseStatuses,
  canViewCourses,
  canAccessUserProfileCheck,
  canAccessProfileSectionCheck,
  onGoToUserProfile,
}) => {
  const classes = useStyles();
  const {
    impression,
    active_plans,
    finished_plans,
    course_todo,
    course_in_progress,
    course_completed,
    notes,
    ...rest
  } = user;
  const [inProgress, Done] = ACTION_PLAN_STATUSES_LIST;
  const [toDo, active, completed] = courseStatuses;
  const totalNotesIndicator = getTotalNotesIndicator(translations.labels.total);
  const userMood = prepareUserMoodData(impression);
  const canAccessUserProfile = canAccessUserProfileCheck(user.id);
  const canAccessOneOnOne = canAccessProfileSectionCheck(
    user.id,
    PERMISSION_CODES.ONE_ON_ONE_VIEW
  );
  const canAccessActionPlans = canAccessProfileSectionCheck(
    user.id,
    PERMISSION_CODES.PLANS_VIEW
  );
  const canAccessCourses = canAccessProfileSectionCheck(
    user.id,
    PERMISSION_CODES.LEARNING_VIEW
  );

  const handleGoToOneOnOne = useCallback(() => {
    if (canAccessOneOnOne) {
      return onGoToUserProfile(user.id, 'one-on-one');
    }

    if (canAccessUserProfile) {
      return onGoToUserProfile(user.id);
    }

    return null;
  }, [user, canAccessOneOnOne, canAccessUserProfile, onGoToUserProfile]);

  const handleGoToPlans = useCallback(() => {
    if (canAccessActionPlans) {
      onGoToUserProfile(user.id, 'action-plans');
    }

    if (canAccessUserProfile) {
      return onGoToUserProfile(user.id);
    }

    return null;
  }, [user, canAccessActionPlans, canAccessUserProfile, onGoToUserProfile]);

  const handleGoToCourses = useCallback(() => {
    if (canAccessCourses) {
      onGoToUserProfile(user.id, 'learning');
    }

    if (canAccessUserProfile) {
      return onGoToUserProfile(user.id);
    }

    return null;
  }, [user, canAccessCourses, canAccessUserProfile, onGoToUserProfile]);

  const handleGoToNotes = useCallback(() => {
    if (canAccessUserProfile) {
      onGoToUserProfile(user.id, 'notes');
    }
  }, [user, canAccessUserProfile, onGoToUserProfile]);

  return (
    <div
      className={classNames(
        classes.root,
        { [classes.rootNoCourses]: !canViewCourses },
        className
      )}
    >
      <div className={classes.headerWrapper}>
        <UserLink
          className={classes.user}
          user={rest}
          avatarProps={{
            variant: 'subtitle1',
            customAvatarClass: classes.avatar,
            labelClass: classes.name,
            customCaptionContainerClass: classes.caption,
            captionDescriptionClass: classes.jobTitle,
            captionDescription: rest.position?.name,
            withDescription: !!rest.position,
            extraSmall: false,
            small: true,
          }}
          isUserAccessible={canAccessUserProfile}
        />
      </div>
      <div className={classes.statsWrapper}>
        <div
          className={classNames(classes.section, {
            [classes.sectionAccessible]:
              canAccessOneOnOne || canAccessUserProfile,
          })}
          onClick={handleGoToOneOnOne}
        >
          <div className={classes.labelWrapper}>
            <Typography className={classes.label} variant="overline">
              {translations.labels.oneOnOnes}
            </Typography>
            <Tooltip
              customIconClass={classes.tooltipIcon}
              text={translations.tooltipMessages.oneOnOnes}
              icon={TooltipIcon}
            />
          </div>
          <GeneralMoodChart data={userMood} />
        </div>
        <div
          className={classNames(classes.section, {
            [classes.sectionAccessible]:
              canAccessActionPlans || canAccessUserProfile,
          })}
          onClick={handleGoToPlans}
        >
          <div className={classes.labelWrapper}>
            <Typography className={classes.label} variant="overline">
              {translations.labels.actionPlans}
            </Typography>
          </div>
          <div
            className={classNames(classes.scoreWrapper, {
              [classes.noResults]: active_plans === 0,
            })}
          >
            <Typography className={classes.scoreValue} variant="overline">
              {active_plans}
            </Typography>
            <StatusIndicator
              status={{ ...inProgress, name: translations.labels.inProgress }}
              isSmall
            />
          </div>
          <div
            className={classNames(classes.scoreWrapper, {
              [classes.noResults]: finished_plans === 0,
            })}
          >
            <Typography className={classes.scoreValue} variant="overline">
              {finished_plans}
            </Typography>
            <StatusIndicator
              status={{ ...Done, name: translations.labels.done }}
              isSmall
            />
          </div>
        </div>
        <div
          className={classNames(classes.section, {
            [classes.sectionAccessible]: canAccessUserProfile,
          })}
          onClick={handleGoToNotes}
        >
          <div className={classes.labelWrapper}>
            <Typography className={classes.label} variant="overline">
              {translations.labels.notes}
            </Typography>
          </div>
          <div
            className={classNames(classes.scoreWrapper, {
              [classes.noResults]: notes === 0,
            })}
          >
            <Typography className={classes.scoreValue} variant="overline">
              {notes}
            </Typography>
            <StatusIndicator status={totalNotesIndicator} isSmall />
          </div>
        </div>
        {canViewCourses && (
          <div
            className={classNames(classes.section, {
              [classes.sectionAccessible]:
                canAccessCourses || canAccessUserProfile,
            })}
            onClick={handleGoToCourses}
          >
            <div className={classes.labelWrapper}>
              <Typography className={classes.label} variant="overline">
                {translations.labels.courses}
              </Typography>
            </div>
            <div
              className={classNames(classes.scoreWrapper, {
                [classes.noResults]: course_todo === 0,
              })}
            >
              <Typography className={classes.scoreValue} variant="overline">
                {course_todo}
              </Typography>
              <StatusIndicator status={toDo} isSmall />
            </div>
            <div
              className={classNames(classes.scoreWrapper, {
                [classes.noResults]: course_in_progress === 0,
              })}
            >
              <Typography className={classes.scoreValue} variant="overline">
                {course_in_progress}
              </Typography>
              <StatusIndicator status={active} isSmall />
            </div>
            <div
              className={classNames(classes.scoreWrapper, {
                [classes.noResults]: course_completed === 0,
              })}
            >
              <Typography className={classes.scoreValue} variant="overline">
                {course_completed}
              </Typography>
              <StatusIndicator status={completed} isSmall />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

TeamUserStatsCard.propTypes = {
  className: PropTypes.string,
  translations: PropTypes.object.isRequired,
  user: PropTypes.object,
  courseStatuses: PropTypes.arrayOf(PropTypes.object).isRequired,
  canViewCourses: PropTypes.bool.isRequired,
  canAccessUserProfileCheck: PropTypes.func.isRequired,
  canAccessProfileSectionCheck: PropTypes.func.isRequired,
  onGoToUserProfile: PropTypes.func.isRequired,
};

export default TeamUserStatsCard;
